import { parametrosDoObjeto } from './parametros'
import type { NovoObjetoTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

/**
 * Ancoragem por envolvimento: uma fita (ou corda) em volta de um tronco, poste
 * ou pilar, com as pontas num mosquetão. Três jeitos:
 *
 *  - **cesta**: a fita abraça o tronco e as duas pontas vão ao mosquetão; as
 *    duas pernas dividem a carga, como num V;
 *  - **enforcado** (boca de lobo): a fita passa por dentro dela mesma e
 *    estrangula o tronco; uma perna só leva toda a carga;
 *  - **três voltas e duas pegadas**: três voltas seguram por atrito e as duas
 *    pegadas vão ao mosquetão; as pernas dividem a carga como na cesta.
 *
 * A peça fica centrada no eixo do tronco, na altura da fita, com o +Z local
 * apontando para o mosquetão (a direção da carga). Na cesta, as pernas saem do
 * tronco tangentes a ele: o ângulo entre elas é `2·asen(r / d)`, com `r` o raio
 * do tronco mais a fita e `d` a distância do eixo ao mosquetão, e cada perna
 * leva `1 / (2·cos(ângulo / 2))` da carga (o mesmo V ideal das ancoragens).
 * Didático: não vale como capacidade da fita nem do tronco.
 */

export const TIPOS_ENVOLVIMENTO = ['cesta', 'enforcado', 'tres-voltas-duas-pegadas'] as const
export type TipoEnvolvimento = (typeof TIPOS_ENVOLVIMENTO)[number]

export const NOMES_ENVOLVIMENTO: Readonly<Record<TipoEnvolvimento, string>> = {
  cesta: 'Cesta',
  enforcado: 'Enforcado (boca de lobo)',
  'tres-voltas-duas-pegadas': 'Três voltas e duas pegadas',
}

/** Espessura da fita, somada ao raio do tronco. */
export const ESPESSURA_FITA_M = 0.012
/** Folga mínima entre o tronco e o mosquetão. */
const FOLGA_MINIMA_M = 0.03

export interface GeometriaEnvolvimento {
  readonly tipo: TipoEnvolvimento
  /** Raio do tronco mais a fita. */
  readonly raioM: number
  /** Do eixo ao mosquetão (pelo menos o raio mais uma folga). */
  readonly distanciaM: number
  /** O mosquetão, no referencial da peça. */
  readonly mestre: VetorTabletop
  /** Onde as pernas deixam o tronco, no referencial da peça: duas na cesta e nas três voltas, uma no enforcado. */
  readonly saidas: readonly VetorTabletop[]
  /** Voltas completas em volta do tronco: 0 na cesta (meia volta), 1 no enforcado, 3 nas três voltas. */
  readonly voltas: number
}

export const tipoDoEnvolvimento = (objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): TipoEnvolvimento =>
  TIPOS_ENVOLVIMENTO[Math.round(parametrosDoObjeto(objeto).tipoEnvolvimento ?? 0)] ?? 'cesta'

export function geometriaDoEnvolvimento(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): GeometriaEnvolvimento {
  const p = parametrosDoObjeto(objeto)
  const tipo = tipoDoEnvolvimento(objeto)
  const raioM = p.diametroM! / 2 + ESPESSURA_FITA_M
  const distanciaM = Math.max(p.distanciaM!, raioM + FOLGA_MINIMA_M)
  const mestre: VetorTabletop = [0, 0, distanciaM]
  if (tipo === 'enforcado') {
    // o estrangulamento fica na frente do tronco, e a perna única sai dali
    return { tipo, raioM, distanciaM, mestre, saidas: [[0, 0, raioM]], voltas: 1 }
  }
  // pontos de tangência vistos do mosquetão: a ±φ do eixo Z, com cos φ = r / d
  const phi = Math.acos(raioM / distanciaM)
  const saidas: VetorTabletop[] = [
    [-raioM * Math.sin(phi), 0, raioM * Math.cos(phi)],
    [raioM * Math.sin(phi), 0, raioM * Math.cos(phi)],
  ]
  return { tipo, raioM, distanciaM, mestre, saidas, voltas: tipo === 'cesta' ? 0 : 3 }
}

export interface AnaliseEnvolvimento {
  readonly tipo: TipoEnvolvimento
  readonly nome: string
  readonly pernas: 1 | 2
  /** Ângulo entre as duas pernas no mosquetão; `null` no enforcado (uma perna só). */
  readonly anguloEntrePernasGraus: number | null
  /** Fração da carga em cada perna. */
  readonly fracaoPorPerna: number
  /** O que o tipo ensina, numa frase. */
  readonly observacao: string
}

const OBSERVACOES: Readonly<Record<TipoEnvolvimento, string>> = {
  cesta:
    'As duas pernas dividem a carga. Quanto mais perto do tronco fica o mosquetão, maior o ângulo entre elas e maior a carga em cada uma. Sem atrito que a segure, a fita pode escorregar no tronco se a carga não puxar na direção dela.',
  enforcado:
    'Uma perna só leva toda a carga, e a fita estrangula o tronco: não escorrega, mas a dobra do estrangulamento diminui a resistência da fita. O quanto diminui é dado do fabricante.',
  'tres-voltas-duas-pegadas':
    'As três voltas seguram por atrito e não deixam a fita escorregar; as duas pegadas vão ao mosquetão e dividem a carga como na cesta.',
}

export function analisarEnvolvimento(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): AnaliseEnvolvimento {
  const g = geometriaDoEnvolvimento(objeto)
  const base = { tipo: g.tipo, nome: NOMES_ENVOLVIMENTO[g.tipo], observacao: OBSERVACOES[g.tipo] }
  if (g.tipo === 'enforcado') return { ...base, pernas: 1, anguloEntrePernasGraus: null, fracaoPorPerna: 1 }
  const meioAngulo = Math.asin(g.raioM / g.distanciaM)
  return {
    ...base,
    pernas: 2,
    anguloEntrePernasGraus: (2 * meioAngulo * 180) / Math.PI,
    fracaoPorPerna: 1 / (2 * Math.cos(meioAngulo)),
  }
}

/**
 * Raio do tronco de uma peça na altura `y` (local), para envolver com a fita.
 * `null` se a peça não tem tronco que se envolva.
 */
export function raioDoTroncoEm(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>, y: number): number | null {
  switch (objeto.tipo) {
    case 'arvore':
      // tronco cônico: 0,34 m de raio no chão, 0,20 m a 3,1 m
      return Math.max(0.2, 0.34 - (0.14 * Math.min(Math.max(y, 0), 3.1)) / 3.1)
    case 'poste':
      return Math.max(0.13, 0.22 - (0.09 * Math.min(Math.max(y, 0), 4.5)) / 4.5)
    case 'toco':
      return parametrosDoObjeto(objeto).diametroM! / 2
    default:
      return null
  }
}

/** Altura (local) em que a fita envolve cada peça, quando criada pelo botão do inspetor. */
export function alturaDoEnvolvimentoEm(objeto: Pick<ObjetoTabletop, 'tipo' | 'parametros'>): number | null {
  switch (objeto.tipo) {
    case 'arvore':
    case 'poste':
      return 1
    case 'toco':
      return parametrosDoObjeto(objeto).alturaM! * 0.6
    default:
      return null
  }
}

/**
 * A fita em volta do tronco de `objeto` (árvore, poste ou toco), pronta para
 * entrar na mesa: na altura de trabalho, com o diâmetro do tronco ali e o
 * mosquetão virado para o mesmo lado que a peça. `null` se a peça não tem tronco.
 */
export function novoEnvolvimentoEm(objeto: ObjetoTabletop): NovoObjetoTabletop | null {
  const altura = alturaDoEnvolvimentoEm(objeto)
  const raio = altura === null ? null : raioDoTroncoEm(objeto, altura)
  if (altura === null || raio === null) return null
  const [x, y, z] = objeto.transformacao.posicao
  const [ex, ey] = objeto.transformacao.escala
  return {
    tipo: 'envolvimento',
    nome: `Fita em volta de ${objeto.nome}`,
    transformacao: {
      posicao: [x, y + altura * ey, z],
      rotacao: [0, objeto.transformacao.rotacao[1], 0],
      escala: [1, 1, 1],
    },
    parametros: { diametroM: Math.round(2 * raio * ex * 100) / 100 },
  }
}
